import Map, { Source, Layer, Popup, Marker } from 'react-map-gl/maplibre';
import type { ViewStateChangeEvent, MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { ExpressionSpecification, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { getActiveEarlyWarning } from './ukAdminApi';
import { ActiveEW, AdminLevel, AreaColors, EWMarker, UkAdminProps, ZoomBand } from './LocationMapTypes';
import { ewIcon, DEFAULT_EW_ICON } from './ewIcons';
import style from './LocationMap.module.css';
import { useTheme } from '../../context/ThemeContext';
import { useAdminData } from '../../context/LocationDataContext';

const DEFAULT_FILL = "#5457a6";
const LINE_COLOR = "#7fa8ff";
const INITIAL_VIEW = { longitude: -3.5, latitude: 55, zoom: 4.6 }
const ICON_SIZE = 28

const darkStyle: StyleSpecification = {
    version: 8,
    sources: {},
    layers: [{ id: "bg", type: "background", paint: { "background-color": "#07072b" } }],
}

const lightStyle: StyleSpecification = {
    version: 8,
    sources: {},
    layers: [{ id: "bg", type: "background", paint: { "background-color": "#ececee" } }],
}

const BANDS: ZoomBand[] = [
    { level: 1, min: 0, max: 5.5, label: "Countries" },
    { level: 2, min: 5.5, max: 7, label: "Counties / unitary authorities" },
    { level: 3, min: 7, max: 9, label: "Districts" },
    { level: 4, min: 9, max: 24, label: "Wards" },
];

// numeric rank so comparisons work (string comparison would rank "yellow" above "red")
const SEVERITY_RANK: Record<string, number> = {
    green: 0, yellow: 1, amber: 2, red: 3,
}

const SEVERITY_COLORS: Record<string, string> = {
    red: "#dc2626",
    amber: "#f59e0b",
    yellow: "#facc15",
    green: "#7f1d1d00",
}

const rankOf = (severity?: string) => SEVERITY_RANK[severity?.toLowerCase() ?? ""] ?? 0

// popup title text per warning type
const WARNING_NAMES: Record<string, string> = {
    HRW: "Heavy rain warning",
    HWN: "High wind warning",
    LIG: "Lightning warning",
    FLW: "Flood warning",
    LSW: "Land slide warning",
    MWA: "Marine warning",
    TW: "Tsunami warning",
    TSW: "Tsunami warning",
    GUS: "Wind gust warning",
    ASW: "Air strike warning",
    NTW: "Nuclear threat warning",
    SNW: "Snow warning",
    ICE: "Ice warning",
    FOG: "Fog warning",
    EXH: "Extreme heat warning",
    THS: "Thunderstorm warning",
    CUS: "Custom warning",
}

const capitalise = (v?: string) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : "")

// "2026-10-26 18:00" in UK time
const ukDateTime = (iso: string) => {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso || "N/A"
    const p = Object.fromEntries(
        new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/London",
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", hour12: false,
        }).formatToParts(d).map(x => [x.type, x.value])
    )
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`
}

interface LocationMapProp {
    gridArea?: string
    colors?: AreaColors
    height?: number | string
    onAreaClick?: (area: UkAdminProps, lngLat: { lng: number; lat: number }) => void
    showActiveWarning?: boolean      // colour areas by warning severity
    showWarningIcons?: boolean       // icons on at start
    allowIconToggle?: boolean        // show the "Show / Hide warnings" button
    cursor?: string                  // map cursor, e.g. "crosshair" while drawing
    children?: ReactNode             // extra layers drawn on this map, e.g. <AcknowledgementLayer />
}

const LocationMap = ({
    gridArea = 'none',
    colors = {},
    height = "100vh",
    onAreaClick,
    showActiveWarning = false,
    showWarningIcons = false,
    allowIconToggle = true,
    cursor = "grab",
    children,
}: LocationMapProp) => {

    const { theme } = useTheme()

    // all 4 levels are loaded once at app open by LocationDataContext
    const { data } = useAdminData()

    const [zoom, setZoom] = useState<number>(INITIAL_VIEW.zoom);
    const [warnings, setWarnings] = useState<ActiveEW[]>([])
    const [iconsOn, setIconsOn] = useState<boolean>(showWarningIcons)
    const [hoverEW, setHoverEW] = useState<ActiveEW | null>(null)   // icon under the mouse

    // add a level to the map just before you zoom into it, then keep it mounted
    const [shown, setShown] = useState<AdminLevel[]>([1])

    useEffect(() => {
        BANDS.forEach(b => {
            if (zoom >= b.min - 0.5 && zoom < b.max) {
                setShown(prev => prev.includes(b.level) ? prev : [...prev, b.level])
            }
        })
    }, [zoom])

    // follow the prop if the parent changes it
    useEffect(() => setIconsOn(showWarningIcons), [showWarningIcons])

    // load warnings when either the colours or the icons need them
    useEffect(() => {
        if (!showActiveWarning && !iconsOn) return

        const controller = new AbortController()
        getActiveEarlyWarning(controller.signal)
            .then(setWarnings)
            .catch(err => {
                if (err.name !== "AbortError") console.error(err)
            })
        return () => controller.abort()
    }, [showActiveWarning, iconsOn])

    // colour areas by severity when warning colours or icons are switched on
    const severityFill = showActiveWarning || iconsOn

    const fillColor = useMemo<ExpressionSpecification | string>(() => {
        if (!severityFill) {
            const entries = Object.entries(colors)
            if (entries.length === 0) return DEFAULT_FILL
            const flat = entries.flat()

            // 1. the area's own colour
            // 2. otherwise its nearest coloured parent: district (adm3), county (adm2), country (adm1)
            //    so every child, grandchild and ward of a coloured area gets the same colour
            const byParent = (prop: string, fallback: unknown) =>
                ["match", ["coalesce", ["get", prop], ""], ...flat, fallback]

            return [
                "match", ["get", "code"], ...flat,
                byParent("adm3", byParent("adm2", byParent("adm1", DEFAULT_FILL))),
            ] as unknown as ExpressionSpecification
        }

        // geoId -> highest severity (lowercase)
        const byGeo = new globalThis.Map<string, string>()
        for (const w of warnings) {
            const sev = w.severity?.trim().toLowerCase()
            if (!sev || !SEVERITY_COLORS[sev]) continue
            const prev = byGeo.get(w.geoId)
            if (!prev || SEVERITY_RANK[sev] > SEVERITY_RANK[prev]) {
                byGeo.set(w.geoId, sev)
            }
        }

        if (byGeo.size === 0) return DEFAULT_FILL

        const pairs = [...byGeo].flatMap(([geoId, sev]) => [geoId, SEVERITY_COLORS[sev]])

        // 1. the warned area itself (any level)
        // 2. otherwise its children: a ward inside a warned district / borough / county
        //    (uses the adm3 / adm2 properties from /map/v1/level/{level})
        const byProp = (prop: string, fallback: unknown) =>
            ["match", ["coalesce", ["get", prop], ""], ...pairs, fallback]

        return [
            "match", ["get", "code"], ...pairs,
            byProp("adm3", byProp("adm2", DEFAULT_FILL)),
        ] as unknown as ExpressionSpecification

    }, [colors, severityFill, warnings])

    // one icon group per area; inside it one icon per warning type (highest severity wins)
    const markers = useMemo<EWMarker[]>(() => {
        const byGeo = new globalThis.Map<string, { lng: number; lat: number; items: globalThis.Map<string, ActiveEW> }>()

        for (const w of warnings) {
            const lat = Number(w.latitude)
            const lng = Number(w.longitude)
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
            if (!ewIcon(w.warningType, w.severity)) continue

            const group = byGeo.get(w.geoId) ?? { lng, lat, items: new globalThis.Map<string, ActiveEW>() }
            const prev = group.items.get(w.warningType)
            if (!prev || rankOf(w.severity) > rankOf(prev.severity)) {
                group.items.set(w.warningType, w)
            }
            byGeo.set(w.geoId, group)
        }

        return [...byGeo].map(([geoId, g]) => ({ geoId, lng: g.lng, lat: g.lat, items: [...g.items.values()] }))
    }, [warnings])

    const fillOpacity = 0.85

    const current = BANDS.find(b => zoom >= b.min && zoom < b.max)

    // area click still works for onAreaClick; there is no area hover popup
    const areaFromEvent = (e: MapLayerMouseEvent): UkAdminProps | null => {
        const f = e.features?.[0];
        return f ? (f.properties as UkAdminProps) : null
    }

    return (
        <div style={{ position: "relative", width: "100%", height, gridArea: gridArea }}>
            <Map
                initialViewState={INITIAL_VIEW}
                style={{ width: "100%", height: "100%", borderRadius: 8 }}
                mapStyle={theme === 'colorful' || theme === 'light' ? lightStyle : darkStyle}
                minZoom={3}
                maxZoom={14}
                interactiveLayerIds={BANDS.map(b => `fill-${b.level}`)}
                onZoom={(e: ViewStateChangeEvent) => setZoom(e.viewState.zoom)}
                onClick={(e: MapLayerMouseEvent) => {
                    const area = areaFromEvent(e);
                    if (area && onAreaClick) onAreaClick(area, { lng: e.lngLat.lng, lat: e.lngLat.lat });
                }}
                attributionControl={false}
                cursor={cursor}
            >
                {BANDS.map(b => {
                    const fc = data?.[b.level];
                    if (!fc || !shown.includes(b.level)) return null;
                    return (
                        <Source key={b.level} id={`uk-${b.level}`} type="geojson" data={fc}>
                            <Layer
                                id={`fill-${b.level}`}
                                type="fill"
                                minzoom={b.min}
                                maxzoom={b.max}
                                paint={{ "fill-color": fillColor, "fill-opacity": fillOpacity }}
                            />
                            <Layer
                                id={`line-${b.level}`}
                                type="line"
                                minzoom={b.min}
                                maxzoom={b.max}
                                paint={{ "line-color": LINE_COLOR, "line-width": 0.8 }}
                            />
                        </Source>
                    );
                })}

                {/* warning icons at each area's location */}
                {iconsOn && markers.map(m => (
                    <Marker key={m.geoId} style={{ zIndex: hoverEW?.geoId === m.geoId ? 2 : 1 }} longitude={m.lng} latitude={m.lat} anchor="bottom">
                        <div style={{ display: "flex", gap: 2 }}>
                            {m.items.map(w => (
                                <img
                                    key={w.warningType}
                                    src={ewIcon(w.warningType, w.severity) ?? DEFAULT_EW_ICON}
                                    alt={`${w.warningType} ${w.severity}`}
                                    title={`${w.name}: ${w.warningType} (${w.severity})`}
                                    width={ICON_SIZE}
                                    height={ICON_SIZE}
                                    style={{ cursor: "pointer", display: "block" }}
                                    onMouseEnter={() => setHoverEW(w)}
                                    onMouseLeave={() => setHoverEW(null)}
                                    onError={(e) => {
                                        if (!e.currentTarget.src.endsWith(DEFAULT_EW_ICON)) e.currentTarget.src = DEFAULT_EW_ICON
                                    }}
                                />
                            ))}
                        </div>
                    </Marker>
                ))}

                {/* details of the icon under the mouse */}
                {hoverEW && (
                    <Popup
                        className={style.EwPopup}
                        longitude={Number(hoverEW.longitude)}
                        latitude={Number(hoverEW.latitude)}
                        anchor="left"
                        offset={[ICON_SIZE / 2 + 6, -ICON_SIZE / 2]}
                        closeButton={false}
                        closeOnClick={false}
                        maxWidth="360px"
                    >
                        <div className={style.Title}>
                            {WARNING_NAMES[hoverEW.warningType?.toUpperCase()] ?? hoverEW.warningType} - {capitalise(hoverEW.status)}
                        </div>
                        <div className={style.Divider} />
                        <div className={style.Grid}>
                            <span className={style.Label}>Area name:</span>
                            <span className={style.Value}>{hoverEW.name}</span>
                            <span className={style.Label}>Area code:</span>
                            <span className={style.Value}>{hoverEW.geoId}</span>
                            <span className={style.Label}>Process id:</span>
                            <span className={style.Value}>{hoverEW.warningId}</span>
                            <span className={style.Label}>Current status:</span>
                            <span className={style.Value}>{hoverEW.status?.toLowerCase()}</span>
                            <span className={style.Label}>Severity:</span>
                            <span className={style.Value}>{capitalise(hoverEW.severity)}</span>
                            <span className={style.Label}>Start date:</span>
                            <span className={style.Value}>{ukDateTime(hoverEW.startDate)}</span>
                            <span className={style.Label}>End date:</span>
                            <span className={style.Value}>{ukDateTime(hoverEW.endDate)}</span>
                        </div>
                    </Popup>
                )}
                {/* extra layers from the parent screen */}
                {children}
            </Map>

            <div style={{
                position: "absolute", top: 10, left: 10, padding: "4px 10px",
                background: "#000a", color: "#c7d2fe", borderRadius: 6, fontSize: 13,
            }}>
                {current?.label} · zoom {zoom.toFixed(1)}
            </div>

            {allowIconToggle && (
                <button
                    onClick={() => {
                        setIconsOn(v => !v)
                        setHoverEW(null)
                    }}
                    style={{
                        position: "absolute", top: 10, right: 10, padding: "4px 10px", border: "none",
                        background: iconsOn ? "#f59e0b" : "#000a", color: iconsOn ? "#000" : "#c7d2fe",
                        borderRadius: 6, fontSize: 13, cursor: "pointer",
                    }}
                >
                    {iconsOn ? "Hide warnings" : "Show warnings"}
                </button>
            )}
        </div>
    )
}

export default LocationMap
